import { Money } from '../components/ui/Money';
import React, { useState, useEffect } from 'react';
import { Calendar, Save, Download, AlertCircle, Lock, Unlock, FileText, Bell, Mail, MessageCircle } from 'lucide-react';
import EmailDocDialog from '../components/documents/EmailDocDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { shareHint } from '../utils/shareInfo';
import { BulkWhatsAppPayslips } from '../components/payroll/BulkWhatsAppPayslips';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const Attendance = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [records, setRecords] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [subscribersWaiting, setSubscribersWaiting] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedRecords, setEditedRecords] = useState({});
  const [emailPayslip, setEmailPayslip] = useState(null);
  const [bulkWaOpen, setBulkWaOpen] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [sifCompanies, setSifCompanies] = useState([]);
  const [showAllCols, setShowAllCols] = useState(false);
  const [shareMap, setShareMap] = useState({});

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = user.role === 'viewer';
  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => {
    loadAttendance();
  }, [year, month]);

  useEffect(() => {
    axios.get(`${API_URL}/api/settings/wps`)
      .then(r => setSifCompanies(r.data?.visa_companies || []))
      .catch(() => setSifCompanies([]));
  }, []);

  const loadAttendance = async () => {
    setLoading(true);
    try {
      const [attendanceRes, payrollRes, subsRes] = await Promise.all([
        axios.get(`${API_URL}/api/attendance/monthly/${year}/${month}`),
        axios.get(`${API_URL}/api/attendance/payroll/${year}/${month}`).catch(() => null),
        axios.get(`${API_URL}/api/attendance/payslip-subscribers/${year}/${month}`).catch(() => null),
      ]);
      setRecords(attendanceRes.data);
      setPayrollSummary(payrollRes?.data || null);
      setSubscribersWaiting(subsRes?.data?.waiting || 0);
      setEditedRecords({});
    } catch (error) {
      console.error('Error loading attendance:', error);
      toast({ title: 'Error', description: 'Failed to load attendance data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCcTipsKeyDown = (e, idx) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const nextIdx = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
      const el = document.querySelector(`[data-cc-tips-index="${nextIdx}"]`);
      if (el) { el.focus(); if (typeof el.select === 'function') el.select(); }
    }
  };

  const handleFieldChange = (employeeId, field, value) => {
    setEditedRecords(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const getRecordValue = (record, field) => {
    if (editedRecords[record.employee_id]?.[field] !== undefined) {
      return editedRecords[record.employee_id][field];
    }
    return record[field] || 0;
  };

  // A column is shown when "Show all columns" is on, or any employee has a
  // non-zero value in it for the selected month — empty columns are hidden to
  // declutter the sheet (e.g. no sick leave this month → no Sick column).
  const showCol = (field) => showAllCols || records.some((r) => Number(getRecordValue(r, field)) > 0);
  const hiddenColCount = ['ot_hours', 'cc_tips', 'additions', 'deductions', 'annual_leave_days',
    'sick_leave_days', 'emergency_leave_days', 'ph_leave_days', 'unpaid_leave_days', 'mgmt_unpaid_leave_days',
    'suspension_days', 'lop_days'].filter((f) => !showCol(f)).length;

  const saveRecord = async (record) => {
    if (payrollSummary?.finalized) {
      toast({ title: 'Error', description: 'Payroll is finalized and cannot be edited', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const editedData = editedRecords[record.employee_id] || {};
      
      // Build request payload - only include days_worked if explicitly edited by user
      const payload = {
        employee_id: record.employee_id,
        year,
        month,
        ot_hours: editedData.ot_hours ?? record.ot_hours ?? 0,
        cc_tips: editedData.cc_tips ?? record.cc_tips ?? 0,
        deductions: editedData.deductions ?? record.deductions ?? 0,
        additions: editedData.additions ?? record.additions ?? 0,
        suspension_days: editedData.suspension_days ?? record.suspension_days ?? 0,
        lop_days: editedData.lop_days ?? record.lop_days ?? 0,
        lop_deduction: editedData.lop_deduction ?? record.lop_deduction ?? 0,
        basic_salary: editedData.basic_salary ?? record.basic_salary,
        housing_allowance: editedData.housing_allowance ?? record.housing_allowance,
        transport_allowance: editedData.transport_allowance ?? record.transport_allowance,
        other_allowance: editedData.other_allowance ?? record.other_allowance
      };
      
      // Only include days_worked if it was explicitly edited (super admin manual override)
      if (editedData.days_worked !== undefined) {
        payload.days_worked = editedData.days_worked;
      }
      
      await axios.post(`${API_URL}/api/attendance/record`, payload);
      toast({ title: 'Success', description: `Attendance saved for ${record.employee_name}` });
      loadAttendance();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save attendance', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const saveAllRecords = async () => {
    if (payrollSummary?.finalized) {
      toast({ title: 'Error', description: 'Payroll is finalized and cannot be edited', variant: 'destructive' });
      return;
    }

    setSaving(true);
    let successCount = 0;
    let failedRecords = [];
    
    try {
      // Process records sequentially to avoid overwhelming the server
      for (const record of records) {
        const editedData = editedRecords[record.employee_id] || {};
        try {
          // Build request payload - only include days_worked if explicitly edited by user
          const payload = {
            employee_id: record.employee_id,
            year,
            month,
            ot_hours: editedData.ot_hours ?? record.ot_hours ?? 0,
            cc_tips: editedData.cc_tips ?? record.cc_tips ?? 0,
            deductions: editedData.deductions ?? record.deductions ?? 0,
            additions: editedData.additions ?? record.additions ?? 0,
            suspension_days: editedData.suspension_days ?? record.suspension_days ?? 0,
            lop_days: editedData.lop_days ?? record.lop_days ?? 0,
            lop_deduction: editedData.lop_deduction ?? record.lop_deduction ?? 0,
            basic_salary: editedData.basic_salary ?? record.basic_salary,
            housing_allowance: editedData.housing_allowance ?? record.housing_allowance,
            transport_allowance: editedData.transport_allowance ?? record.transport_allowance,
            other_allowance: editedData.other_allowance ?? record.other_allowance
          };
          
          // Only include days_worked if it was explicitly edited (super admin manual override)
          if (editedData.days_worked !== undefined) {
            payload.days_worked = editedData.days_worked;
          }
          
          await axios.post(`${API_URL}/api/attendance/record`, payload);
          successCount++;
        } catch (err) {
          failedRecords.push(record.employee_name);
          console.error(`Failed to save for ${record.employee_name}:`, err.response?.data || err.message);
        }
      }
      
      if (failedRecords.length === 0) {
        toast({ title: 'Success', description: 'All attendance records saved' });
      } else if (successCount > 0) {
        toast({ 
          title: 'Partial Success', 
          description: `Saved ${successCount} records. Failed: ${failedRecords.slice(0, 3).join(', ')}${failedRecords.length > 3 ? '...' : ''}`,
          variant: 'default'
        });
      } else {
        toast({ title: 'Error', description: 'Failed to save all records', variant: 'destructive' });
      }
      loadAttendance();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save records', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const saveMonthlyPayroll = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/attendance/save-monthly/${year}/${month}`);
      toast({ title: 'Success', description: response.data.message });
      loadAttendance();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save monthly payroll', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const finalizePayroll = async () => {
    if (!window.confirm(`Are you sure you want to finalize payroll for ${MONTHS[month-1].label} ${year}? This cannot be undone.`)) {
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/attendance/finalize/${year}/${month}`);
      toast({ title: 'Success', description: 'Payroll finalized successfully' });
      loadAttendance();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to finalize payroll', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Unlock finalized payroll (Super Admin only)
  const unlockPayroll = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/attendance/unlock/${year}/${month}`);
      toast({ title: 'Success', description: 'Payroll unlocked for editing' });
      setShowUnlockDialog(false);
      loadAttendance();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to unlock payroll', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Recalculate all attendance (Super Admin only)
  const recalculateAll = async () => {
    if (!window.confirm(`Recalculate all attendance for ${MONTHS[month-1].label} ${year}? This will update Days Worked based on Leave data.`)) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/attendance/recalculate-all/${year}/${month}`);
      toast({ 
        title: 'Success', 
        description: response.data.message
      });
      loadAttendance();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to recalculate', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Build download URL
  const getPayslipUrl = (employeeId, payType = 'monthly', format = 'pdf') => {
    return `${API_URL}/api/downloads/payslip/${employeeId}?year=${year}&month=${month}&pay_type=${payType}&format=${format}`;
  };

  // One-tap WhatsApp: mint a public payslip link for the selected month and open wa.me.
  const whatsappPayslip = async (record) => {
    try {
      const r = await axios.post(`${API_URL}/api/downloads/payslip/${record.employee_id}/share-link?year=${year}&month=${month}`);
      const { token: tok, mobile, name, period, net, share_count, last_shared_at } = r.data || {};
      setShareMap((m) => ({ ...m, [record.employee_id]: { share_count, last_shared_at } }));
      const url = `${API_URL}/api/downloads/public/payslip/${tok}?format=pdf`;
      const msg = `Hi ${name || ''}, here is your payslip for ${period}.\n`
        + `Net Pay: AED ${Number(net || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.\n`
        + `View / download: ${url}`;
      const digits = String(mobile || '').replace(/[^0-9]/g, '');
      if (!digits) toast({ title: 'No mobile on file', description: 'Opening WhatsApp without a number — pick the contact manually.' });
      else toast({ title: 'WhatsApp link ready', description: `${shareHint({ share_count, last_shared_at })} · link valid 30 days` });
      window.open(digits ? `https://wa.me/${digits}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    } catch {
      toast({ title: 'Could not create WhatsApp link', variant: 'destructive' });
    }
  };

  const getLeaveSalaryUrl = (employeeId, format = 'pdf') => {
    return `${API_URL}/api/downloads/leave-salary/${employeeId}?year=${year}&format=${format}`;
  };

  const totalGross = records.reduce((sum, r) => sum + (r.gross_salary || 0), 0);
  const totalNet = records.reduce((sum, r) => sum + (r.net_salary || 0), 0);
  const totalLOP = records.reduce((sum, r) => sum + (r.lop_deduction || 0), 0);

  // Salary breakdown by payment method group
  const sharedNet = records.filter(r => r.payment_method === 'SHARED').reduce((s, r) => s + (r.net_salary || 0), 0);
  const mgmtNet = records.filter(r => r.payment_method === 'SG').reduce((s, r) => s + (r.net_salary || 0), 0);
  const snbNet = records.filter(r => r.payment_method !== 'SHARED' && r.payment_method !== 'SG').reduce((s, r) => s + (r.net_salary || 0), 0);

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold">Payroll</h1>
          <p className="text-gray-600 mt-1">Monthly payroll and attendance records</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Payroll Sheet Download (Excel / PDF) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="payroll-download-btn">
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Payroll Sheet — {MONTHS[month-1].label} {year}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="payroll-download-excel"
                onSelect={() => { window.location.href = `${API_URL}/api/downloads/payroll-sheet/${year}/${month}?format=excel`; }}
              >
                <Download className="h-4 w-4 mr-2 text-green-600" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="payroll-download-pdf"
                onSelect={() => { window.location.href = `${API_URL}/api/downloads/payroll-sheet/${year}/${month}?format=pdf`; }}
              >
                <FileText className="h-4 w-4 mr-2 text-purple-600" />
                PDF (.pdf)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Generate SIF (WPS) per visa company */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="sif-generate-btn">
                <FileText className="h-4 w-4 mr-2" />
                SIF (WPS)
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Generate SIF — {MONTHS[month-1].label} {year}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {sifCompanies.length === 0 ? (
                <DropdownMenuItem disabled>No visa companies configured</DropdownMenuItem>
              ) : (
                sifCompanies.map(vc => (
                  <DropdownMenuItem
                    key={vc}
                    data-testid={`sif-company-${vc}`}
                    onSelect={() => { window.location.href = `${API_URL}/api/downloads/sif/${year}/${month}?visa_company=${encodeURIComponent(vc)}`; }}
                  >
                    <Download className="h-4 w-4 mr-2 text-blue-600" />
                    {vc}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Bulk WhatsApp Payslips */}
          <Button
            variant="outline"
            className="text-green-700 border-green-300 hover:bg-green-50"
            onClick={() => setBulkWaOpen(true)}
            disabled={records.length === 0}
            data-testid="bulk-whatsapp-payslips-btn"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Bulk WhatsApp
          </Button>

          {/* Month/Year Selector */}
          <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!isViewer && !payrollSummary?.finalized && (
            <>
              {isSuperAdmin && (
                <Button onClick={recalculateAll} disabled={saving} variant="outline" className="text-blue-600 border-blue-300">
                  <Calendar className="h-4 w-4 mr-2" />
                  Recalculate All
                </Button>
              )}
              <Button onClick={saveAllRecords} disabled={saving} variant="outline">
                <Save className="h-4 w-4 mr-2" />
                Save All
              </Button>
              <Button onClick={saveMonthlyPayroll} disabled={saving}>
                <Calendar className="h-4 w-4 mr-2" />
                Save Monthly Payroll
              </Button>
            </>
          )}
          
          {payrollSummary && !payrollSummary.finalized && !isViewer && (
            <div className="flex items-center gap-2" data-testid="finalize-with-subscribers">
              {subscribersWaiting > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-50 text-amber-800 border border-amber-300 rounded-md"
                  title={`${subscribersWaiting} employee${subscribersWaiting === 1 ? '' : 's'} will receive a "Payslip ready" push the moment you click Finalize.`}
                  data-testid="subscribers-waiting-badge"
                >
                  <Bell className="h-3.5 w-3.5" />
                  {subscribersWaiting} waiting
                </span>
              )}
              <Button onClick={finalizePayroll} disabled={saving} variant="destructive">
                <Lock className="h-4 w-4 mr-2" />
                Finalize
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Status Badge */}
      {payrollSummary?.finalized && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-green-600" />
            <span className="text-green-800 font-medium">
              Payroll for {MONTHS[month-1].label} {year} is finalized
            </span>
          </div>
          {isSuperAdmin && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowUnlockDialog(true)}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
              data-testid="unlock-payroll-btn"
            >
              <Unlock className="h-4 w-4 mr-1" />
              Unlock for Editing
            </Button>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total Employees</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{records.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total Gross Salary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-blue-600"><Money value={totalGross} /></p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total LOP Deduction</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-600"><Money value={totalLOP} /></p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total Net Salary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600"><Money value={Math.round(totalNet)} /></p>
          </CardContent>
        </Card>
      </div>

      {/* Net Salary Breakdown by Payment Group */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-indigo-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-indigo-600">Shared</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold text-indigo-700"><Money value={Math.round(sharedNet)} /></p>
            <p className="text-[10px] text-gray-400">{records.filter(r => r.payment_method === 'SHARED').length} employees</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-600">MGMT</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold text-amber-700"><Money value={Math.round(mgmtNet)} /></p>
            <p className="text-[10px] text-gray-400">{records.filter(r => r.payment_method === 'SG').length} employees</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-emerald-600">SNB Staffs</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold text-emerald-700"><Money value={Math.round(snbNet)} /></p>
            <p className="text-[10px] text-gray-400">{records.filter(r => r.payment_method !== 'SHARED' && r.payment_method !== 'SG').length} employees</p>
          </CardContent>
        </Card>
      </div>

      {/* Attendance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Payroll Sheet - {MONTHS[month-1].label} {year}</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs font-normal"
                onClick={() => setShowAllCols((v) => !v)}
                data-testid="toggle-all-columns-btn"
                title="Empty columns (no data this month) are hidden to save space. Toggle to show every column — needed to enter values into a hidden column."
              >
                {showAllCols
                  ? 'Hide empty columns'
                  : `Show all columns${hiddenColCount ? ` (${hiddenColCount} hidden)` : ''}`}
              </Button>
              <Badge variant={payrollSummary?.finalized ? "default" : "outline"}>
                {payrollSummary?.finalized ? "Finalized" : "Draft"}
              </Badge>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="overflow-x-auto overflow-y-auto max-h-[65vh]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-gray-100">
                    <th className="p-2 text-left sticky left-0 bg-gray-100 z-30">Employee</th>
                    <th className="p-2 text-center bg-green-50" title="Super Admin: Editable | Others: Auto-calculated from leaves">Days Worked</th>
                    {showCol('ot_hours') && <th className="p-2 text-center bg-gray-100">OT Hours</th>}
                    {showCol('cc_tips') && <th className="p-2 text-center bg-gray-100">CC Tips</th>}
                    {showCol('additions') && <th className="p-2 text-center bg-gray-100">Additions</th>}
                    {showCol('deductions') && <th className="p-2 text-center bg-gray-100">Deductions</th>}
                    {showCol('annual_leave_days') && <th className="p-2 text-center bg-blue-50">Annual Leave</th>}
                    {showCol('sick_leave_days') && <th className="p-2 text-center bg-blue-50">Sick</th>}
                    {showCol('emergency_leave_days') && <th className="p-2 text-center bg-rose-50" title="Paid emergency days. Days HR marks unpaid move to Unpaid (LOP).">Emergency</th>}
                    {showCol('ph_leave_days') && <th className="p-2 text-center bg-purple-50">PH</th>}
                    {showCol('unpaid_leave_days') && <th className="p-2 text-center bg-blue-50">Unpaid</th>}
                    {showCol('mgmt_unpaid_leave_days') && <th className="p-2 text-center bg-amber-50">MGMT Unpaid</th>}
                    {showCol('suspension_days') && <th className="p-2 text-center bg-orange-50">Suspension</th>}
                    {showCol('lop_days') && <th className="p-2 text-center bg-red-50">LOP Days</th>}
                    <th className="p-2 text-center bg-gray-100">Gross</th>
                    <th className="p-2 text-center text-red-600 bg-gray-100">LOP Ded.</th>
                    <th className="p-2 text-center text-green-600 bg-gray-100">Net Salary</th>
                    <th className="p-2 text-center bg-purple-50">Payslip</th>
                    {!isViewer && !payrollSummary?.finalized && <th className="p-2 text-center bg-gray-100">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, idx) => (
                    <tr key={record.employee_id} className="border-b hover:bg-gray-50">
                      <td className="p-2 sticky left-0 bg-white z-10">
                        <div>
                          <p className="font-medium">{record.employee_name}</p>
                          <p className="text-xs text-gray-500">{record.employee_code}</p>
                        </div>
                      </td>
                      <td className="p-2 text-center bg-green-50">
                        {isSuperAdmin && !payrollSummary?.finalized ? (
                          <Input 
                            type="number"
                            className="w-16 h-8 text-center mx-auto bg-green-50 font-medium text-green-700"
                            value={getRecordValue(record, 'days_worked')}
                            onChange={(e) => handleFieldChange(record.employee_id, 'days_worked', e.target.value)}
                            disabled={payrollSummary?.finalized}
                            min={0}
                            max={31}
                            step="0.5"
                            title="Editable by Super Admin"
                          />
                        ) : (
                          <span 
                            className="inline-block w-16 h-8 leading-8 text-center font-medium text-green-700"
                            title="Auto-calculated from leaves"
                          >
                            {record.days_worked}
                          </span>
                        )}
                      </td>
                      {showCol('ot_hours') && (
                      <td className="p-2 text-center">
                        <Input 
                          type="number"
                          className="w-16 h-8 text-center mx-auto"
                          value={getRecordValue(record, 'ot_hours')}
                          onChange={(e) => handleFieldChange(record.employee_id, 'ot_hours', e.target.value)}
                          disabled={isViewer || payrollSummary?.finalized}
                          min={0}
                          step="0.5"
                        />
                      </td>
                      )}
                      {showCol('cc_tips') && (
                      <td className="p-2 text-center">
                        <Input 
                          type="number"
                          className="w-20 h-8 text-center mx-auto"
                          value={getRecordValue(record, 'cc_tips')}
                          onChange={(e) => handleFieldChange(record.employee_id, 'cc_tips', e.target.value)}
                          onKeyDown={(e) => handleCcTipsKeyDown(e, idx)}
                          data-cc-tips-index={idx}
                          disabled={isViewer || payrollSummary?.finalized}
                          min={0}
                        />
                      </td>
                      )}
                      {showCol('additions') && (
                      <td className="p-2 text-center">
                        <Input 
                          type="number"
                          className="w-20 h-8 text-center mx-auto"
                          value={getRecordValue(record, 'additions')}
                          onChange={(e) => handleFieldChange(record.employee_id, 'additions', e.target.value)}
                          disabled={isViewer || payrollSummary?.finalized}
                          min={0}
                        />
                      </td>
                      )}
                      {showCol('deductions') && (
                      <td className="p-2 text-center">
                        <Input 
                          type="number"
                          className="w-20 h-8 text-center mx-auto"
                          value={getRecordValue(record, 'deductions')}
                          onChange={(e) => handleFieldChange(record.employee_id, 'deductions', e.target.value)}
                          disabled={isViewer || payrollSummary?.finalized}
                          min={0}
                        />
                      </td>
                      )}
                      {showCol('annual_leave_days') && (
                      <td className="p-2 text-center bg-blue-50">
                        <Badge variant="outline" className="bg-blue-100">{record.annual_leave_days || 0}</Badge>
                      </td>
                      )}
                      {showCol('sick_leave_days') && (
                      <td className="p-2 text-center bg-blue-50">
                        <Badge variant="outline" className="bg-yellow-100">{record.sick_leave_days || 0}</Badge>
                      </td>
                      )}
                      {showCol('emergency_leave_days') && (
                      <td className="p-2 text-center bg-rose-50" data-testid={`payroll-emergency-${record.employee_code}`}>
                        <Badge variant="outline" className="bg-rose-100">{record.emergency_leave_days || 0}</Badge>
                        {record.emergency_pay_pending > 0 && (
                          <p className="text-[10px] text-amber-700 font-semibold mt-0.5" title="Counted as paid until HR decides paid / unpaid on the dashboard">Pending HR decision</p>
                        )}
                      </td>
                      )}
                      {showCol('ph_leave_days') && (
                      <td className="p-2 text-center bg-purple-50">
                        <Badge variant="outline" className="bg-purple-100">{record.ph_leave_days || 0}</Badge>
                      </td>
                      )}
                      {showCol('unpaid_leave_days') && (
                      <td className="p-2 text-center bg-blue-50">
                        <Badge variant="outline" className="bg-gray-100">{record.unpaid_leave_days || 0}</Badge>
                      </td>
                      )}
                      {showCol('mgmt_unpaid_leave_days') && (
                      <td className="p-2 text-center bg-amber-50">
                        <Badge variant="outline" className={`${(record.mgmt_unpaid_leave_days || 0) > 0 ? 'bg-amber-200 text-amber-800 border-amber-400' : 'bg-amber-50'}`}>
                          {record.mgmt_unpaid_leave_days || 0}
                        </Badge>
                      </td>
                      )}
                      {showCol('suspension_days') && (
                      <td className="p-2 text-center bg-orange-50">
                        <Input 
                          type="number"
                          className="w-16 h-8 text-center mx-auto bg-orange-50"
                          value={getRecordValue(record, 'suspension_days')}
                          onChange={(e) => handleFieldChange(record.employee_id, 'suspension_days', e.target.value)}
                          disabled={isViewer || payrollSummary?.finalized}
                          min={0}
                          step="0.5"
                        />
                      </td>
                      )}
                      {showCol('lop_days') && (
                      <td className="p-2 text-center bg-red-50">
                        <Input 
                          type="number"
                          className="w-16 h-8 text-center mx-auto bg-red-50"
                          value={getRecordValue(record, 'lop_days')}
                          onChange={(e) => handleFieldChange(record.employee_id, 'lop_days', e.target.value)}
                          disabled={isViewer || payrollSummary?.finalized}
                          min={0}
                          step="0.5"
                        />
                      </td>
                      )}
                      <td className="p-2 text-center font-medium">
                        {record.gross_salary?.toLocaleString() || 0}
                      </td>
                      <td className="p-2 text-center">
                        <Input 
                          type="number"
                          className="w-20 h-8 text-center mx-auto text-red-600"
                          value={getRecordValue(record, 'lop_deduction')}
                          onChange={(e) => handleFieldChange(record.employee_id, 'lop_deduction', e.target.value)}
                          disabled={isViewer || payrollSummary?.finalized}
                          min={0}
                        />
                      </td>
                      <td className="p-2 text-center text-green-600 font-bold">
                        {Math.round(record.net_salary || 0).toLocaleString()}
                      </td>
                      {/* Payslip Download Column */}
                      <td className="p-2 text-center bg-purple-50">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-8 px-2 border-purple-300 hover:bg-purple-100"
                              data-testid={`payslip-dropdown-${record.employee_id}`}
                            >
                              <FileText className="h-4 w-4 text-purple-600" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Download Payslip</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs text-gray-500">Pay Type</DropdownMenuLabel>
                            <DropdownMenuItem
                              data-testid={`payslip-monthly-pdf-${record.employee_id}`}
                              onSelect={() => { window.location.href = getPayslipUrl(record.employee_id, 'monthly', 'pdf'); }}
                            >
                              <FileText className="h-4 w-4 mr-2 text-purple-600" />
                              Monthly (PDF)
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`payslip-weekly-pdf-${record.employee_id}`}
                              onSelect={() => { window.location.href = getPayslipUrl(record.employee_id, 'weekly', 'pdf'); }}
                            >
                              <FileText className="h-4 w-4 mr-2 text-blue-600" />
                              Weekly (PDF)
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`payslip-daily-pdf-${record.employee_id}`}
                              onSelect={() => { window.location.href = getPayslipUrl(record.employee_id, 'daily', 'pdf'); }}
                            >
                              <FileText className="h-4 w-4 mr-2 text-green-600" />
                              Daily (PDF)
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs text-gray-500">Excel Format</DropdownMenuLabel>
                            <DropdownMenuItem
                              data-testid={`payslip-monthly-excel-${record.employee_id}`}
                              onSelect={() => { window.location.href = getPayslipUrl(record.employee_id, 'monthly', 'excel'); }}
                            >
                              <Download className="h-4 w-4 mr-2 text-green-600" />
                              Monthly (Excel)
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              data-testid={`payslip-email-${record.employee_id}`}
                              onSelect={() => setEmailPayslip(record)}
                            >
                              <Mail className="h-4 w-4 mr-2 text-indigo-600" />
                              Email to staff
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`payslip-whatsapp-${record.employee_id}`}
                              onSelect={() => whatsappPayslip(record)}
                            >
                              <MessageCircle className="h-4 w-4 mr-2 text-green-600" />
                              <span className="flex flex-col">
                                <span>WhatsApp to staff</span>
                                {shareMap[record.employee_id]?.share_count > 0 && (
                                  <span className="text-[10px] text-gray-400" data-testid={`payslip-share-hint-${record.employee_id}`}>
                                    {shareHint(shareMap[record.employee_id])}
                                  </span>
                                )}
                              </span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                      {!isViewer && !payrollSummary?.finalized && (
                        <td className="p-2 text-center">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => saveRecord(record)}
                            disabled={saving}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="pt-4">
          <div className="space-y-2 text-sm">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-100 rounded border border-green-300"></div>
                <span className="font-medium">Days Worked {isSuperAdmin ? '(editable for Super Admin)' : '(auto-calculated)'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-100 rounded"></div>
                <span>Leave (from Leave section)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-100 rounded"></div>
                <span>PH Days (from Leave section)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-100 rounded border border-orange-300"></div>
                <span>Suspension Days (editable)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-100 rounded border border-red-300"></div>
                <span>LOP Days (editable)</span>
              </div>
            </div>
            <div className="border-t pt-2 mt-2">
              <p className="text-gray-600">
                <strong>Formula:</strong> Days Worked = Days in Month - Annual - Sick - PH - Unpaid - MGMT Unpaid - Suspension
              </p>
              <p className="text-gray-600">
                <strong>Net Salary:</strong> (Days Worked + Annual + Sick + PH) × Daily Rate + CC Tips + Additions - Deductions
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Unlock Confirmation Dialog */}
      <AlertDialog open={showUnlockDialog} onOpenChange={setShowUnlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
              <Unlock className="h-5 w-5" />
              Unlock Finalized Payroll
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-2">
                You are about to unlock the finalized payroll for <strong>{MONTHS[month-1].label} {year}</strong>.
              </p>
              <p className="text-orange-600 font-medium">
                Warning: This will allow editing of financial records. Use with caution.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={unlockPayroll}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={saving}
            >
              {saving ? 'Unlocking...' : 'Unlock Payroll'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EmailDocDialog
        open={Boolean(emailPayslip)}
        onOpenChange={(o) => { if (!o) setEmailPayslip(null); }}
        title={`Email Payslip — ${MONTHS[month-1].label} ${year}`}
        recipientName={emailPayslip?.employee_name}
        attachmentLabel="payslip"
        payload={{ doc_type: 'payslip', employee_id: emailPayslip?.employee_id, year, month, pay_type: 'monthly' }}
      />
      <BulkWhatsAppPayslips
        open={bulkWaOpen}
        onClose={() => setBulkWaOpen(false)}
        records={records}
        year={year}
        month={month}
        monthLabel={`${MONTHS[month-1].label} ${year}`}
      />
    </div>
  );
};

export default Attendance;
